import React, { useState, useEffect, useRef } from 'react';
import { COLORS } from '../App';
import DocumentActionPanel from './DocumentActionPanel';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';

const REPORT_TYPES = [
  { key: 'appraisal', label: 'Appraisal', extractionLive: true, desc: 'MAI appraisal report — value, cap rates, comp sales and rents extracted' },
  { key: 'pca', label: 'Property Condition Assessment (PCA)', extractionLive: false, desc: 'Physical condition report from qualified engineer' },
  { key: 'esa', label: 'Environmental Site Assessment (ESA)', extractionLive: false, desc: 'Phase I or Phase II environmental report' },
  { key: 'seismic', label: 'Seismic Report', extractionLive: false, desc: 'Seismic risk assessment (required in Zones 3 & 4)' },
];

function UploadZone({ onFiles, disabled }) {
  const inputRef = useRef();
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false);
        if (!disabled) { const files = Array.from(e.dataTransfer.files); if (files.length) onFiles(files); }
      }}
      onClick={() => !disabled && inputRef.current.click()}
      style={{
        border: `2px dashed ${dragOver ? COLORS.gold : COLORS.border}`,
        borderRadius: 6, padding: '14px 20px', textAlign: 'center',
        cursor: disabled ? 'default' : 'pointer',
        background: dragOver ? '#FFF9EC' : COLORS.white,
        transition: 'all 0.15s', opacity: disabled ? 0.5 : 1,
      }}
    >
      <input ref={inputRef} type="file" accept=".pdf" onChange={(e) => {
        const files = Array.from(e.target.files);
        if (files.length) onFiles(files);
        e.target.value = '';
      }} style={{ display: 'none' }} />
      <span style={{ fontSize: 18, color: COLORS.textLight }}>+</span>
      <span style={{ fontSize: 12, color: COLORS.textLight, marginLeft: 8 }}>Drop PDF or click to upload</span>
    </div>
  );
}

export default function ThirdPartyReports({ deal, onUpdate }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState({});
  const token = localStorage.getItem('cmbs_token');

  useEffect(() => { loadDocs(); }, [deal.id]);

  const loadDocs = async () => {
    const res = await fetch(`${API_BASE}/diligence/documents/${deal.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json());
    if (Array.isArray(res)) {
      setDocs(res.filter(d => d.doc_category === 'third_party_report'));
    }
  };

  const handleUpload = async (subcategory, files) => {
    setUploading(u => ({ ...u, [subcategory]: true }));
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('deal_id', deal.id);
      fd.append('doc_category', 'third_party_report');
      fd.append('doc_subcategory', subcategory);
      try {
        await fetch(`${API_BASE}/diligence/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
      } catch (err) {
        alert('Upload failed: ' + err.message);
      }
    }
    await loadDocs();
    setUploading(u => ({ ...u, [subcategory]: false }));
  };

  const docsForType = (type) => docs.filter(d => d.doc_type === type);

  return (
    <div>
      <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 8 }}>Third-Party Reports</h3>
      <p style={{ color: COLORS.textLight, fontSize: 13, marginBottom: 20 }}>
        Upload and extract data from appraisals and other third-party reports. Appraisal extraction is live; PCA, ESA, and Seismic extraction coming soon.
      </p>

      {REPORT_TYPES.map(rt => {
        const typeDocs = docsForType(rt.key);
        return (
          <div key={rt.key} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, margin: 0 }}>{rt.label}</h4>
              {rt.extractionLive ? (
                <span style={{ fontSize: 10, fontWeight: 700, background: '#DCFCE7', color: '#166534', padding: '2px 8px', borderRadius: 10 }}>Extraction Live</span>
              ) : (
                <span style={{ fontSize: 10, color: COLORS.textLight, background: '#F3F4F6', padding: '2px 8px', borderRadius: 10 }}>Extraction Coming Soon</span>
              )}
            </div>
            <p style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 10 }}>{rt.desc}</p>

            <UploadZone onFiles={(files) => handleUpload(rt.key, files)} disabled={uploading[rt.key]} />
            {uploading[rt.key] && <p style={{ fontSize: 12, color: COLORS.textLight, marginTop: 6 }}>Uploading...</p>}

            {typeDocs.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {typeDocs.map(doc => (
                  <DocumentActionPanel
                    key={doc.id}
                    doc={doc}
                    deal={deal}
                    token={token}
                    onRefresh={loadDocs}
                    isAppraisal={rt.key === 'appraisal'}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
